import { useState, useMemo, useCallback } from 'react';
import type { ForceGraphData, ForceNode, ForceLink } from '../types/graph';
import { GRAPH_CONFIG } from '../types/graph';

/**
 * Extract the first directory segment from a node ID.
 * "Mobile NILAssist/composables/core/useLogger.ts" → "composables"
 * "Mobile NILAssist/App.vue" → null (root file)
 */
function getDirectory(nodeId: string): string | null {
  // Strip service prefix: "Service Name/path/to/file"
  const slashIdx = nodeId.indexOf('/');
  if (slashIdx === -1) return null;
  const relativePath = nodeId.substring(slashIdx + 1);
  const parts = relativePath.split('/');
  // If there's a directory (more than just filename), return first dir
  return parts.length > 1 ? parts[0] : null;
}

/**
 * Build a cluster key from service + directory.
 * e.g. "Mobile NILAssist" + "composables" → "Mobile NILAssist/composables"
 */
function clusterKey(service: string, dir: string): string {
  return `${service}/${dir}`;
}

export function useClusteredGraph(rawData: ForceGraphData, enabled: boolean) {
  const [expandedClusters, setExpandedClusters] = useState<Set<string>>(new Set());

  const toggleCluster = useCallback((key: string) => {
    setExpandedClusters(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const clusteredData: ForceGraphData = useMemo(() => {
    if (!enabled || rawData.nodes.length === 0) return rawData;

    // Step 1: Group nodes by cluster key
    const clusterGroups = new Map<string, ForceNode[]>();
    const rootNodes: ForceNode[] = []; // Files without a directory

    for (const node of rawData.nodes) {
      const dir = getDirectory(node.id);
      if (dir) {
        const key = clusterKey(node.service, dir);
        if (!clusterGroups.has(key)) clusterGroups.set(key, []);
        clusterGroups.get(key)!.push(node);
      } else {
        rootNodes.push(node);
      }
    }

    // Step 2: Build output nodes — clusters (collapsed) or files (expanded/single)
    const outputNodes: ForceNode[] = [...rootNodes];
    const collapsedNodeToCluster = new Map<string, string>(); // nodeId → clusterKey

    for (const [key, children] of clusterGroups) {
      if (children.length <= 1) {
        // Single file in directory — no cluster, just show the file
        outputNodes.push(...children);
        continue;
      }

      if (expandedClusters.has(key)) {
        // Expanded — show individual files
        outputNodes.push(...children);
      } else {
        // Collapsed — create cluster node
        const service = children[0].service;
        const dir = key.substring(key.indexOf('/') + 1);
        const clusterNode: ForceNode = {
          id: `cluster:${key}`,
          name: `${dir}/ (${children.length})`,
          service,
          language: children[0].language,
          type: 'cluster',
          val: GRAPH_CONFIG.nodeValCluster(children.length),
          color: GRAPH_CONFIG.serviceColors[service] || '#666666',
          _isCluster: true,
          _childCount: children.length,
          _clusterKey: key
        };
        outputNodes.push(clusterNode);

        // Map child IDs to this cluster
        for (const child of children) {
          collapsedNodeToCluster.set(child.id, `cluster:${key}`);
        }
      }
    }

    // Step 3: Remap edges — replace collapsed node IDs with cluster IDs
    const outputNodeIds = new Set(outputNodes.map(n => n.id));
    const edgeDedup = new Set<string>();
    const outputLinks: ForceLink[] = [];

    for (const link of rawData.links) {
      let src = collapsedNodeToCluster.get(link.source as string) || link.source as string;
      let tgt = collapsedNodeToCluster.get(link.target as string) || link.target as string;

      // Skip self-loops within the same cluster
      if (src === tgt) continue;

      // Skip edges where either endpoint is missing (shouldn't happen)
      if (!outputNodeIds.has(src) || !outputNodeIds.has(tgt)) continue;

      // Dedup (multiple file edges may map to the same cluster edge)
      const edgeKey = `${src}→${tgt}`;
      if (edgeDedup.has(edgeKey)) continue;
      edgeDedup.add(edgeKey);

      outputLinks.push({
        source: src,
        target: tgt,
        type: link.type,
        color: link.color,
        width: (src.startsWith('cluster:') || tgt.startsWith('cluster:')) ? GRAPH_CONFIG.edgeWidthCluster : link.width
      });
    }

    return { nodes: outputNodes, links: outputLinks };
  }, [rawData, enabled, expandedClusters]);

  return {
    clusteredData,
    expandedClusters,
    toggleCluster,
  };
}
